import { memo, useState, useRef, useMemo } from 'react';
import { Button, Card, ModalProps, Form, Input, message, Spin, Table, Popconfirm } from 'antd';
import { css } from '@emotion/css';
import { useTranslate } from '@tolgee/react';
import { JustRightHeightModal } from 'components/styled/scoped';
import useModal from 'hooks/useModal';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get } from 'lodash-es';
import Empty from 'components/Empty';
import { useSize } from 'ahooks';
import {
  deleteEasyRefundBuyer,
  EasyRefundBuyerListResult,
  getEasyRefundBuyerList,
  GetEasyRefundBuyerListParams,
  saveEasyRefundBuyer,
} from 'services/api/easyrefund';
import dayjs from 'dayjs';

const EasyRefundBuyer = memo(() => {
  const { t } = useTranslate();
  const { visible, hide, show } = useModal();
  const [buyerNick, setBuyerNick] = useState<string>('');
  const [pageInfo, setPageInfo] = useState({ pageNo: 1, pageSize: 10 });
  const searchParams = useMemo(() => {
    return {
      ...pageInfo,
      buyerNick,
    };
  }, [buyerNick, pageInfo]);
  const reachedDataRef = useRef(false);
  const { data, isLoading, refetch } = useQuery(
    ['easyRefundBuyer', searchParams],
    ({ queryKey }) => {
      const parmas = queryKey[1] as GetEasyRefundBuyerListParams;
      return getEasyRefundBuyerList(parmas);
    },
    {
      onSuccess: (data) => {
        if (!!get(data, 'total')) {
          reachedDataRef.current = true;
        }
      },
      onError: () => {
        message.error(t('phrase.获取数据失败，请稍后再试'));
      },
    },
  );
  const noData = Number(get(data, 'total')) === 0;
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const height = useSize(tableContainerRef)?.height || 0;
  const queryClient = useQueryClient();

  return (
    <div className="p-[16px] flex flex-col h-full overflow-hidden">
      {visible && (
        <AddBuyer
          visible={visible}
          onSuccess={() => {
            refetch();
            hide();
            queryClient.invalidateQueries({ queryKey: ['getAllEasyRefundBuyer'] });
          }}
          onCancel={() => hide()}
        />
      )}
      <Card
        title={
          <div>
            {t('managementCenter.navigation.buyer.highRefundRiskBuyers')}
            <span className="text-[14px] text-[#84868C] font-normal ml-[4px]">
              {t('仅对Shopee-跟单助手-弃单挽回-仅退款/退货退款挽回策略生效')}
            </span>
          </div>
        }
        className={css`
          height: 100%;
          .ant-card-head-title {
            padding: 12px 0;
          }
          .ant-card-body {
            height: calc(100% - 57px);
            overflow: hidden;
          }
          .ant-card-extra {
            padding: 12px 0;
          }
        `}
        extra={
          <Button type="primary" onClick={() => show()}>
            + {t('添加买家')}
          </Button>
        }
      >
        <div className="h-full" ref={tableContainerRef}>
          {isLoading && !reachedDataRef.current ? (
            <div className="h-full flex flex-col m-auto justify-center">
              <Spin />
            </div>
          ) : noData && !reachedDataRef.current ? (
            <div className="h-full flex flex-col m-auto justify-center">
              <Empty description={t('ViewDashboard.暂无数据')}>
                <Button onClick={() => show()} type="primary">
                  + {t('添加买家')}
                </Button>
              </Empty>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-end mb-[12px]">
                <div className="w-[280px]">
                  <Input.Search
                    placeholder={t('market.请输入买家昵称')}
                    onSearch={(value) => {
                      setBuyerNick(value);
                    }}
                  />
                </div>
              </div>
              <Table<EasyRefundBuyerListResult['regularRefundList'][number]>
                columns={[
                  { key: 'buyerNick', title: t('ViewBlockList.买家昵称'), dataIndex: 'buyerNick' },
                  { key: 'operatorName', title: t('添加人'), dataIndex: 'operatorName' },
                  {
                    key: 'createTime',
                    title: t('WhatsApp.table.添加时间'),
                    dataIndex: 'createTime',
                    render: (text) => {
                      return dayjs(Number(text)).format('YYYY-MM-DD HH:mm:ss');
                    },
                  },
                  {
                    key: 'id',
                    title: t('操作'),
                    dataIndex: 'id',
                    render: (id) => {
                      return (
                        <RemoveBuyer
                          id={id}
                          onSuccess={() => {
                            refetch();
                            queryClient.invalidateQueries({ queryKey: ['getAllEasyRefundBuyer'] });
                          }}
                        />
                      );
                    },
                  },
                ]}
                dataSource={get(data, 'regularRefundList', [])}
                scroll={{ y: height - 144 }}
                pagination={{
                  pageSize: pageInfo.pageSize,
                  showTotal: (total) => `${t('feedback.共x项', { x: total })}`,
                  showQuickJumper: true,
                  showSizeChanger: true,
                  current: pageInfo.pageNo,
                  total: Number(get(data, 'total', 0)),
                  onChange: (pageNo, pageSize) => {
                    setPageInfo({ pageNo, pageSize });
                  },
                }}
              />
            </>
          )}
        </div>
      </Card>
    </div>
  );
});

const RemoveBuyer = memo((props: { id: number; onSuccess: () => void }) => {
  const { t } = useTranslate();
  const { id, onSuccess } = props;
  const { mutate, isLoading } = useMutation({
    mutationFn: (id: number) => {
      return deleteEasyRefundBuyer(id);
    },
    onSuccess: () => {
      message.success(t('phrase.删除成功'));
      onSuccess();
    },
    onError: () => {
      message.error(t('phrase.删除失败，请稍后再试'));
    },
  });
  return (
    <Popconfirm
      title={t('你确定要移除吗')}
      onConfirm={() => {
        mutate(id);
      }}
    >
      <Button loading={isLoading} type="link" className="!px-0">
        {t('single_remove')}
      </Button>
    </Popconfirm>
  );
});
RemoveBuyer.displayName = 'RemoveBuyer';

EasyRefundBuyer.displayName = 'EasyRefundBuyer';

export default EasyRefundBuyer;

interface AddBuyerProps extends ModalProps {
  onSuccess: () => void;
}
const AddBuyer = memo((props: AddBuyerProps) => {
  const { onSuccess, ...restProps } = props;
  const { t } = useTranslate();
  const [value, setValue] = useState<string>('');
  const { isLoading, mutateAsync } = useMutation({
    mutationFn: (nicks: string[]) => {
      return saveEasyRefundBuyer({ buyerNick: nicks });
    },
    onError: () => {
      message.error(t('translation.操作失败，请稍后再试'));
    },
  });
  return (
    <JustRightHeightModal
      title="添加买家"
      className={css`
        .ant-modal-body {
          padding: 16px;
        }
      `}
      okButtonProps={{
        disabled: !value,
        loading: isLoading,
      }}
      onOk={() => {
        mutateAsync(
          value
            .split(/[,，\n]/)
            .filter(Boolean)
            .map((e) => e.trim()),
        ).then(() => {
          onSuccess();
        });
      }}
      {...restProps}
    >
      <Form layout="vertical">
        <Form.Item label={t('ViewBlockList.买家昵称')} required>
          <Input.TextArea
            placeholder={t('请输入买家昵称，多个买家以换行或逗号分隔')}
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
            }}
            rows={8}
            showCount
            maxLength={1000}
          />
        </Form.Item>
      </Form>
    </JustRightHeightModal>
  );
});
